import shared from "../data/js/shared";
import { useUser, useUI } from "../contexts";
import { isOverPriceThreshold, isNonShippable } from '@/utils/listing-buyability';
import { canAcceptPayments } from '../data/js/trust-levels';
import Link from "next/link";
import { DATA_SOURCE } from "../data/js/data-sources";
import { ARCHIVED, PURCHASED, HELD, SOLD, WATCHER_STATUS_LABELS } from "../data/js/listing-statuses";

const {
  requestingLabel,
  contactShelterLabel,
  viewDealLabel,
  clickToChatLabel,
  unavailableSign,
  pendingSaleSign,
  interestedLabel,
  GCC_DATASOURCE,
  MYEV_DATASOURCE,
  LOTLINX_DATASOURCE,
  AMAZON_DATASOURCE,
  BARGAINHUNTER_DATASOURCE,
  SPORTSERVER_DATASOURCE,
} = shared.product;


interface ConversionProduct {
  id: string;
  price: number;
  dataSourceId?: string | number;
  categoryId?: string;
  categoryGroupId?: string;
  shippable?: number;
  status?: number;
  remote_url?: string;
  user?: {
    id: string;
    trustLevel?: number;
  };
}

interface ConversionButtonProps {
  product: ConversionProduct;
  requesting: boolean;
  setChatOpen: (open: boolean) => void;
  clickEmailButton: (e: React.MouseEvent) => void;
  contactShelterButton: (e: React.MouseEvent) => void;
  openRemoteUrlButton: (e: React.MouseEvent) => void;
  checkoutUrl?: string;
}

const ConversionButton = ({
  product,
  requesting,
  setChatOpen,
  clickEmailButton,
  contactShelterButton,
  openRemoteUrlButton,
  checkoutUrl,
}: ConversionButtonProps) => {
  const { profile } = useUser();
  const { setLoginOpen } = useUI();

  if (!product) return "";

  // Allow price of 0 for adoption listings (RescueGroups), otherwise require price
  const productDataSourceId = parseInt(String(product?.dataSourceId));
  const isAdoptionListing = productDataSourceId === DATA_SOURCE.RESCUEGROUPS;
  if (!isAdoptionListing && !product.price) return "";
  // const isOrganicListing = product.dataSourceId == "1" ? true : false;
  const isMyEV = productDataSourceId === MYEV_DATASOURCE;

  const overPriceThreshold = isOverPriceThreshold(product.price);
  const nonShippable = isNonShippable(product as Parameters<typeof isNonShippable>[0]);

  // An archived listing is gone, but a cached product page (ISR, or a feed
  // expiry that did not revalidate) can still render it. It used to fall
  // through to Buy Now / Contact Shelter. Shoppers see it the way watchers do:
  // "Taken". Strict equality on purpose: a product with no status at all is
  // not known to be archived and keeps its normal button.
  if (product.status === ARCHIVED) {
    return (
      <div className="product-unavailable-button" data-testid="product-archived-sign">
        <span>{WATCHER_STATUS_LABELS[ARCHIVED]}</span>
      </div>
    );
  }

  // A sale in progress: PURCHASED (3) or HELD (4). Checkout refuses both.
  if (product.status === PURCHASED || product.status === HELD) {
    return (
      <div className="product-unavailable-button">
        <span>{pendingSaleSign}</span>
      </div>
    );
  }

  if ((product?.status ?? 0) >= SOLD) {
    return (
      <div className="product-unavailable-button">
        <span>{unavailableSign}</span>
      </div>
    );
  }

  // Gateway Classic Cars - Remote URL click-out
  if (productDataSourceId === GCC_DATASOURCE) {
    return (
      <div className="email-only-button">
        <div className="email-contact">
          <div className="blueButton" onClick={openRemoteUrlButton}>
            {viewDealLabel}
          </div>
        </div>
      </div>
    );
  }

  // MyEV - Email contact
  if (isMyEV) {
    return (
      <div className="email-only-button">
        <div className="email-contact">
          <div className="blueButton" onClick={clickEmailButton}>
            {clickToChatLabel}
          </div>
        </div>
      </div>
    );
  }

  if (productDataSourceId === BARGAINHUNTER_DATASOURCE || productDataSourceId === SPORTSERVER_DATASOURCE) {
    return (
      <div className="email-only-button">
        <div className="email-contact">
          <div className="blueButton" onClick={openRemoteUrlButton}>
            {viewDealLabel}
          </div>
        </div>
      </div>
    );
  }

  if (productDataSourceId === DATA_SOURCE.RESCUEGROUPS) {
    // If remote_url exists, open it; otherwise fall back to description modal
    const hasRemoteUrl = product.remote_url && product.remote_url.startsWith('http');
    return (
      <div className="email-only-button">
        <div className="email-contact">
          <div className="blueButton" onClick={hasRemoteUrl ? openRemoteUrlButton : contactShelterButton}>
            {contactShelterLabel}
          </div>
        </div>
      </div>
    );
  }

  if (productDataSourceId === LOTLINX_DATASOURCE || productDataSourceId === AMAZON_DATASOURCE) {
    return (
      <div className="email-only-button">
        <div className="email-contact">
          <div className="blueButton" onClick={openRemoteUrlButton}>
            {viewDealLabel}
          </div>
        </div>
      </div>
    );
  }

  return overPriceThreshold ||
    nonShippable ||
    !canAcceptPayments(product?.user?.trustLevel) ? (
    <div
      className="interest-button"
      data-testid="message-seller-button"
      onClick={() => {
        if (!profile?.id) {
          setLoginOpen(true);
          return;
        }
        setChatOpen(true);
      }}
    >
      <span>{requesting ? requestingLabel : clickToChatLabel}</span>
    </div>
  ) : (
    <Link className="buy-now-button" href={checkoutUrl || ("/checkout/" + product?.id)} data-testid="product-buy-btn">
      <span>{interestedLabel}</span>
    </Link>
  );
};

export default ConversionButton;
